import React from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "../Datas/productData";
import "../css/terms.scss";

const termsNavigation = [
  ["terms-demo", "Using this store"],
  ["terms-orders", "Orders and contract"],
  ["terms-products", "Products and pricing"],
  ["terms-payment", "Payment"],
  ["terms-delivery", "Delivery"],
  ["terms-cancellation", "Cancellation and returns"],
  ["terms-faults", "Faults and refunds"],
  ["terms-use", "Site use and liability"],
  ["terms-general", "General terms"],
  ["terms-contact", "Contact"],
];

function Terms() {
  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="terms">
      <div className="terms__shell">
        <nav className="terms__breadcrumb" aria-label="Breadcrumb">
          <Link to="/" onClick={toTop}>Home</Link>
          <span aria-hidden="true">/</span>
          <span aria-current="page">Terms &amp; conditions</span>
        </nav>

        <header className="terms__header">
          <p className="terms__kicker">Legal information</p>
          <h1>Terms &amp; conditions</h1>
          <p className="terms__updated">Last reviewed 23 October 2026</p>
          <p className="terms__lede">
            These terms explain the basis on which {siteConfig.brandName} supplies products through this website.
            Please read them before placing an order.
          </p>
        </header>

        <aside className="terms__demo" aria-label="Important notice">
          <span aria-hidden="true">i</span>
          <p>
            <strong>Please check your order carefully.</strong> Product choice, delivery details and contact email
            should be complete and accurate before you continue to the hosted payment page.
          </p>
        </aside>

        <div className="terms__layout">
          <nav className="terms__contents" aria-label="Terms and conditions contents">
            <p>On this page</p>
            <ol>
              {termsNavigation.map(([id, label]) => (
                <li key={id}><a href={`#${id}`}>{label}</a></li>
              ))}
            </ol>
          </nav>

          <article className="terms__content">
            <section id="terms-demo" className="terms__section">
              <p className="terms__number">01</p>
              <h2>Using this store and eligibility</h2>
              <p>
                The catalogue, cart and checkout are provided for customers in the supported delivery countries
                listed at checkout. Product availability, delivery estimates and order status are shown in good faith
                and may change before an order is accepted.
              </p>
              <p>
                Orders may be placed only by people aged 18 or over, or by a person with the
                permission of a responsible adult, who can provide valid delivery and payment information.
              </p>
            </section>

            <section id="terms-orders" className="terms__section">
              <p className="terms__number">02</p>
              <h2>Orders and formation of a contract</h2>
              <p>
                Placing an order is an offer to buy. An automated acknowledgement confirms receipt; a contract forms
                when we separately accept the order, normally on
                dispatch. The customer should check the basket and delivery information before submitting it.
              </p>
              <p>
                We may decline or cancel an order before acceptance for legitimate reasons such as lack
                of stock, an obvious pricing error, failed payment checks, delivery restrictions or suspected misuse.
                Any money already captured should then be returned to the original payment method.
              </p>
            </section>

            <section id="terms-products" className="terms__section">
              <p className="terms__number">03</p>
              <h2>Products, availability and pricing</h2>
              <p>
                Product photographs aim to represent colour and scale, but screens vary and packaging may change.
                Dimensions, materials, nib sizes, paint characteristics and age guidance should be checked in the
                product description. Stock indicators may change while an item remains in the cart.
              </p>
              <p>
                Prices are shown in {siteConfig.currency}. A fixed tracked-delivery charge of
                {" "}{siteConfig.currencySymbol}{siteConfig.shipping.standard.toFixed(2)} is added before payment.
                Any duties, taxes or carrier charges required by the destination are the customer&apos;s responsibility unless checkout states otherwise.
              </p>
              <p>
                If a displayed price or description is clearly wrong, we will contact the customer before
                acceptance rather than silently substituting a product or price.
              </p>
            </section>

            <section id="terms-payment" className="terms__section">
              <p className="terms__number">04</p>
              <h2>Payment and promotions</h2>
              <p>
                Payment is completed on a hosted payment page. Card and wallet credentials are entered with the payment
                provider rather than on the {siteConfig.brandName} address form. Customers must be authorised to use the
                selected payment method, and an order is not confirmed until payment succeeds and the order is accepted.
              </p>
              <p>
                Promotion codes should be used within their stated dates and conditions, once per order unless noted.
                They should not be exchangeable for cash and should not be combined where an offer says otherwise.
                Refunds should reflect the amount actually paid for each returned item.
              </p>
            </section>

            <section id="terms-delivery" className="terms__section">
              <p className="terms__number">05</p>
              <h2>Delivery</h2>
              <p>
                Customers are responsible for providing a complete, accurate and accessible delivery address. Any
                dates shown are estimates unless expressly guaranteed. Risk would normally pass on physical delivery
                to the customer or a person they identify to receive the goods.
              </p>
              <p>
                See the <Link to="/shipping" onClick={toTop}>delivery information</Link> for current estimates.
                We will provide available tracking details and contact customers about material delays.
              </p>
            </section>

            <section id="terms-cancellation" className="terms__section">
              <p className="terms__number">06</p>
              <h2>Cancellation and change-of-mind returns</h2>
              <p>
                Where EU distance-contract rules apply, a customer normally has 14 days from receipt to notify the
                retailer of withdrawal without giving a reason, followed by a further 14 days to send eligible goods
                back. The item may be inspected as it would be in a shop; excessive handling may reduce the refund.
              </p>
              <p>
                Personalised or made-to-order items, including engraved pens, cannot normally be cancelled once work
                begins unless they are faulty or not as described. Other statutory exclusions may apply to unsealed
                hygiene-sensitive or licensed goods. See the <Link to="/returns" onClick={toTop}>returns policy</Link>.
              </p>
            </section>

            <section id="terms-faults" className="terms__section">
              <p className="terms__number">07</p>
              <h2>Faulty goods, refunds and ownership</h2>
              <p>
                Where EU consumer rules apply, new goods include a minimum two-year legal guarantee and must conform
                to the contract, match their description and be fit for their ordinary or agreed purpose. Customers
                should report damage, shortages or faults with the order details and helpful photographs. Nothing in
                these terms limits mandatory consumer rights.
              </p>
              <p>
                Approved refunds should go to the original payment method within the applicable timeframe. Ownership
                would pass only after the retailer receives full payment, while responsibility for loss normally passes
                on delivery as described above.
              </p>
            </section>

            <section id="terms-use" className="terms__section">
              <p className="terms__number">08</p>
              <h2>Site use, intellectual property and liability</h2>
              <p>
                Visitors may use the site for personal shopping research. They must not interfere with security,
                scrape the service abusively, submit unlawful content, impersonate another person or use checkout for
                fraudulent activity. Site code, editorial copy and original imagery remain protected by applicable
                intellectual-property rights.
              </p>
              <p>
                We do not exclude liability that cannot lawfully be excluded, including liability for fraud or
                mandatory consumer protections. Subject to those protections, we are not responsible for losses that
                were not reasonably foreseeable or for business losses arising from consumer use of the store.
              </p>
            </section>

            <section id="terms-general" className="terms__section">
              <p className="terms__number">09</p>
              <h2>General terms, changes and governing law</h2>
              <p>
                If one clause is unenforceable, the rest should continue. Delay in enforcing a right should not waive
                it. Customers may not transfer a contract without consent; the retailer may transfer it only where this
                does not reduce customer rights. No third party gains enforcement rights unless these terms expressly say so.
              </p>
              <p>
                These terms may change prospectively for legal, operational or service reasons; the version
                applying to an accepted order remains identifiable. The governing-law and jurisdiction details have
                not been verified by the store operator and are therefore not stated here. This does not remove any
                mandatory rights a customer has under the law that applies to them.
              </p>
            </section>

            <section id="terms-contact" className="terms__section terms__section--contact">
              <p className="terms__number">10</p>
              <h2>Questions about these terms</h2>
              <p>
                Contact {siteConfig.brandName} at
                {" "}<a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a>. The store&apos;s
                {" "}business address is {siteConfig.contact.address}, and its operating country is {siteConfig.operatingCountry}.
              </p>
              <Link className="terms__button" to="/contact" onClick={toTop}>
                Contact us <span aria-hidden="true">→</span>
              </Link>
            </section>
          </article>
        </div>
      </div>
    </main>
  );
}

export default Terms;
